import type { ScriptDoc } from './config';

interface DocNode {
  type?: unknown;
  text?: unknown;
  children?: unknown;
}

/** Recursion bound matching the server's doc-tree walk (internal/campaigns
 *  maxDocNodeDepth), so client and server reject the same pathological depth. */
const MAX_DOC_NODE_DEPTH = 64;

const LINE_BREAK_NODE_TYPE = 'linebreak';
const LIST_NODE_TYPE = 'list';
const NEWLINE = '\n';
/** Top-level blocks are separated by a blank line, and empty blocks are dropped,
 *  matching the server's extractPlainTextFromState (internal/campaigns/script_doc_text.go)
 *  so both sides count the same characters against MAX_SCRIPT_CHARS. */
const BLOCK_SEPARATOR = '\n\n';

const isList = (node: DocNode): boolean => node.type === LIST_NODE_TYPE;

/** Concatenates inline children in order, starting a nested list on its own
 *  line so "item" + nested "sub" never fuses into "itemsub". */
function joinInlineChildren(children: DocNode[], depth: number): string {
  let out = '';
  for (const child of children) {
    const text = nodeText(child, depth);
    const needsBreakBeforeList = isList(child) && out.length > 0 && !out.endsWith(NEWLINE);
    if (needsBreakBeforeList) out += NEWLINE;
    out += text;
  }
  return out;
}

/** Collects the text of a Lexical node subtree, depth-bounded. Soft line
 *  breaks become newlines; list items are separated by newlines. */
function nodeText(node: DocNode, depth: number): string {
  if (depth > MAX_DOC_NODE_DEPTH) return '';
  if (node.type === LINE_BREAK_NODE_TYPE) return NEWLINE;
  if (typeof node.text === 'string') return node.text;
  if (!Array.isArray(node.children)) return '';
  const children = node.children as DocNode[];
  if (isList(node)) return children.map((item) => nodeText(item, depth + 1)).join(NEWLINE);
  return joinInlineChildren(children, depth + 1);
}

/** Best-effort plain text of a script doc, for the editor's character count and
 *  empty-check gate. '' for an empty/absent doc. */
export function plainTextFromDoc(doc: ScriptDoc | null | undefined): string {
  const root = (doc as { root?: DocNode } | null | undefined)?.root;
  if (!root || !Array.isArray(root.children)) return '';
  return (root.children as DocNode[])
    .map((block) => nodeText(block, 0))
    .filter((blockText) => blockText !== '')
    .join(BLOCK_SEPARATOR);
}
